import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Mail, Phone, Globe, ShieldCheck, Layers, Share2, BarChart3, Check, ArrowRight, Star, Lock, Gauge,
} from "lucide-react";
import { Nav, Footer, VerifyWidget, Section, FaqList, STATUS } from "@/components";
import { Seo, http, useAuth, ORG_LD, SITE } from "@/lib";

function Shell({ children }) {
  return <div className="min-h-screen flex flex-col"><Nav /><main className="flex-1">{children}</main><Footer /></div>;
}

const LOGOS = ["Northwind", "Hyperline", "Vaulted", "Orbital", "Quartz", "Meridian"];
const STATS = [["99.9%", "Verification uptime"], ["<300ms", "Avg response"], ["3-in-1", "Email · Phone · IP"], ["200+", "Countries covered"]];

const FEATURES = [
  { Icon: Layers, t: "One credit, three signals", d: "Email, phone and IP verification under a single universal credit. Stop juggling three vendors and three invoices." },
  { Icon: Zap, t: "Instant in-browser tool", d: "Verify a value the moment you land — no SDK, no setup. Single and bulk CSV checks run live in your browser." },
  { Icon: Share2, t: "Shareable result pages", d: "Every result gets a permanent, branded /r/ page with a one-tap share. Turn verifications into organic reach." },
  { Icon: Gauge, t: "0–100 trust score", d: "A single, tunable verdict — valid, risky or invalid — so you can branch your signup flow with one if-statement." },
  { Icon: BarChart3, t: "Dashboard & history", d: "Track usage against your limit, browse every past result, and export. Full visibility, zero spreadsheets." },
  { Icon: Lock, t: "Live MX & proxy data", d: "Real DNS MX resolution, disposable-domain detection and proxy/VPN/datacenter flags — not stale databases." },
];

const DIFF = [
  ["Email, phone AND IP in one credit", true, false],
  ["Recurring free tier (50/mo)", true, false],
  ["Instant browser bulk tool", true, false],
  ["Shareable, indexable result pages", true, false],
  ["Flat universal pricing", true, false],
  ["Setup in under 5 minutes", true, "Often"],
];

function HeroSection({ user }) {
  return (
    <div className="relative grid-bg noise border-b border-border">
      <Section className="py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 border border-border px-3 py-1 text-xs font-mono mb-6">
            <span className="w-2 h-2 bg-valid animate-pulse" /> LIVE · 3-in-1 verification engine
          </div>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Verify every signup.<br /><span className="text-accent">Block every fraud.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-md">
            Real-time email, phone and IP risk scoring through one clean API and one universal credit. Catch fake users before they cost you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={user ? "/dashboard" : "/signup"} className="btn-accent px-6 py-3 flex items-center gap-2" data-testid="hero-cta">
              Start free <ArrowRight size={18} />
            </Link>
            <Link to="/pricing" className="btn-ghost px-6 py-3" data-testid="hero-pricing">See pricing</Link>
          </div>
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={`star-${i}`} size={15} className="fill-accent text-accent" />)}</div>
            4.9/5 · 50 free verifications/mo · no card
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} data-testid="hero-verification-widget">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-mono">Try it now — no signup</div>
          <VerifyWidget demo />
        </motion.div>
      </Section>
    </div>
  );
}

function LogoStrip() {
  return (
    <Section className="py-10 border-b border-border">
      <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Trusted by data-driven teams</p>
      <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4 opacity-60 font-display font-bold text-lg">
        {LOGOS.map((l) => <span key={l}>{l}</span>)}
      </div>
    </Section>
  );
}

function StatsStrip() {
  return (
    <Section className="py-16 grid grid-cols-2 lg:grid-cols-4 border-b border-border">
      {STATS.map(([v, l]) => (
        <div key={l} className="text-center px-4 py-4 border-r last:border-r-0 border-border">
          <div className="font-display font-black text-4xl text-accent">{v}</div>
          <div className="text-sm text-muted-foreground mt-1">{l}</div>
        </div>
      ))}
    </Section>
  );
}

function FeatureGrid() {
  return (
    <Section className="py-24">
      <div className="max-w-2xl mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-3">The complete suite</p>
        <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight">Everything you need to trust your data.</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 border-l border-t border-border">
        {FEATURES.map((f) => (
          <motion.div key={f.t} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="border-r border-b border-border p-8 hover:bg-secondary transition-colors group">
            <f.Icon className="text-accent mb-4" size={26} />
            <h3 className="font-display font-bold text-xl mb-2">{f.t}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{f.d}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function DiffSection() {
  return (
    <div className="border-y border-border bg-card">
      <Section className="py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-3">Why switch</p>
          <h2 className="font-display font-bold text-4xl tracking-tight mb-4">The legacy tools do one thing. Verix does all three.</h2>
          <p className="text-muted-foreground mb-6">Most verification vendors sell you email <em>or</em> phone <em>or</em> IP — each with its own contract and credit pack. Verix consolidates everything into one fast API with honest pricing.</p>
          <Link to="/compare" className="btn-primary px-5 py-3 inline-flex items-center gap-2">Compare all 10 competitors <ArrowRight size={16} /></Link>
        </div>
        <div className="border border-border" data-testid="comparison-table">
          <div className="grid grid-cols-[1fr_auto_auto] text-sm">
            <div className="p-3 font-display font-bold border-b border-border">Capability</div>
            <div className="p-3 font-display font-bold border-b border-l border-border text-accent text-center">Verix</div>
            <div className="p-3 font-display font-bold border-b border-l border-border text-muted-foreground text-center">Others</div>
            {DIFF.map(([label, a, b]) => (
              <React.Fragment key={label}>
                <div className="p-3 border-b border-border text-muted-foreground">{label}</div>
                <div className="p-3 border-b border-l border-border text-center">{a === true ? <Check className="text-valid inline" size={18} /> : a}</div>
                <div className="p-3 border-b border-l border-border text-center text-muted-foreground">{b === false ? "—" : b}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}

function BottomCta() {
  return (
    <Section className="py-24 text-center">
      <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">Stop paying for bad data.</h2>
      <p className="text-muted-foreground mt-4 max-w-md mx-auto">Start free in under 5 minutes. 50 verifications a month, no card required.</p>
      <Link to="/signup" className="btn-accent px-8 py-4 mt-8 inline-flex items-center gap-2 text-lg" data-testid="bottom-cta">
        Create free account <ArrowRight size={20} />
      </Link>
    </Section>
  );
}

export function Home() {
  const { user } = useAuth();
  return (
    <Shell>
      <Seo title={null} description="Verix verifies email, phone and IP addresses in real time and returns a 0–100 fraud risk score. Free tier, one universal credit, instant API." path="/"
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Verix", applicationCategory: "SecurityApplication", operatingSystem: "Web", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" }, aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "512" } }} />
      <HeroSection user={user} />
      <LogoStrip />
      <StatsStrip />
      <FeatureGrid />
      <DiffSection />
      <BottomCta />
    </Shell>
  );
}

export function Features() {
  return (
    <Shell>
      <Seo title="Features" description="Verix features: real-time email, phone and IP verification, 0–100 trust scores, bulk tool, shareable results, dashboard and a developer-friendly API." path="/features" />
      <Section className="py-20">
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-4">One API. Every signal.</h1>
        <p className="text-muted-foreground text-lg max-w-2xl">Verix runs the same multi-layer verification the fraud teams at large platforms build in-house — without the build.</p>
      </Section>
      {[
        { Icon: Mail, t: "Email verification", d: "Full RFC 5322 syntax, live MX record resolution, disposable-domain and role-account detection, free-provider classification. Know if an address can actually receive mail before you send.", points: ["Live DNS MX lookup", "300+ disposable domains", "Role & free-provider flags", "Deliverability verdict"] },
        { Icon: Phone, t: "Phone verification", d: "Powered by Google's libphonenumber across 200+ countries. Get validity, line type (mobile/fixed/VoIP), carrier, region and timezone — VoIP numbers flagged as higher risk.", points: ["200+ country coverage", "Line-type detection", "Carrier & region", "E.164 / national formats"] },
        { Icon: Globe, t: "IP intelligence", d: "Real-time geolocation plus proxy, VPN and datacenter-hosting detection. Spot anonymized and automated traffic before it touches your product.", points: ["Proxy / VPN flags", "Datacenter detection", "City-level geolocation", "ISP & ASN data"] },
      ].map((f, i) => (
        <div key={f.t} className={`border-t border-border ${i % 2 ? "bg-card" : ""}`}>
          <Section className="py-16 grid lg:grid-cols-2 gap-12 items-center">
            <div className={i % 2 ? "lg:order-2" : ""}>
              <f.Icon className="text-accent mb-4" size={32} />
              <h2 className="font-display font-bold text-3xl mb-3">{f.t}</h2>
              <p className="text-muted-foreground mb-6">{f.d}</p>
              <ul className="grid grid-cols-2 gap-2">
                {f.points.map((p) => <li key={p} className="flex items-center gap-2 text-sm"><Check className="text-valid" size={16} /> {p}</li>)}
              </ul>
            </div>
            <div className={i % 2 ? "lg:order-1" : ""}><VerifyWidget demo /></div>
          </Section>
        </div>
      ))}
      <Section className="py-20 text-center">
        <Link to="/signup" className="btn-accent px-8 py-4 inline-flex items-center gap-2">Start verifying free <ArrowRight /></Link>
      </Section>
    </Shell>
  );
}

const PRICE_FAQ = [
  { q: "What counts as one credit?", a: "Any single verification — one email, one phone, or one IP check — uses exactly one credit. No hidden multipliers." },
  { q: "Do credits roll over?", a: "Credits reset monthly. Paid plans give you a large monthly allowance designed to comfortably cover production usage." },
  { q: "Can I cancel anytime?", a: "Yes. Manage or cancel your plan instantly from the billing portal — no contracts, no sales calls." },
  { q: "Is there a free tier?", a: "Yes — 50 verifications every month, forever, with no credit card required." },
  { q: "Is payment secure?", a: "Payments are processed by Stripe. Verix never stores your card details." },
];

export function Pricing() {
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  const tiers = [
    { id: "free", name: "Free", price: 0, credits: "50", blurb: "For testing & side projects", features: ["50 verifications/mo", "Email · Phone · IP", "Browser tool", "Shareable results", "Community support"], cta: "Start free", to: "/signup" },
    { id: "pro", name: "Pro", price: annual ? 182 : 19, credits: "5,000", blurb: "For growing products", features: ["5,000 verifications/mo", "Everything in Free", "Bulk CSV verification", "Full history & export", "AI risk summaries", "Priority email support"], cta: "Upgrade to Pro", popular: true },
    { id: "business", name: "Business", price: annual ? 948 : 99, credits: "50,000", blurb: "For high-volume teams", features: ["50,000 verifications/mo", "Everything in Pro", "Highest rate limits", "Dedicated support", "Custom integrations"], cta: "Upgrade to Business" },
  ];
  return (
    <Shell>
      <Seo title="Pricing" description="Simple, universal-credit pricing. Free 50/mo, Pro at $19/mo for 5,000 checks, Business at $99/mo for 50,000. Email, phone and IP under one credit." path="/pricing"
        jsonLd={{ "@context": "https://schema.org", "@type": "Product", name: "Verix", offers: [{ "@type": "Offer", name: "Pro", price: "19", priceCurrency: "USD" }, { "@type": "Offer", name: "Business", price: "99", priceCurrency: "USD" }] }} />
      <Section className="py-20 text-center">
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-4">One credit. Every check.</h1>
        <p className="text-muted-foreground text-lg max-w-xl mx-auto">No tiers of confusion. One universal credit works for email, phone and IP alike.</p>
        <div className="inline-flex items-center gap-3 mt-8 border border-border p-1">
          <button onClick={() => setAnnual(false)} className={`px-4 py-2 text-sm ${!annual ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`} data-testid="billing-monthly">Monthly</button>
          <button onClick={() => setAnnual(true)} className={`px-4 py-2 text-sm ${annual ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`} data-testid="billing-annual">Annual <span className="text-xs">−20%</span></button>
        </div>
      </Section>
      <Section className="pb-20 grid md:grid-cols-3 gap-8">
        {tiers.map((t) => (
          <PricingCard key={t.id} t={t} annual={annual} user={user} />
        ))}
      </Section>
      <Section className="pb-24 max-w-3xl">
        <h2 className="font-display font-bold text-3xl mb-8 text-center">Pricing FAQ</h2>
        <FaqList faqs={PRICE_FAQ} />
      </Section>
    </Shell>
  );
}

async function startCheckout(t, annual, user, setLoading) {
  if (t.id === "free") { window.location.href = user ? "/dashboard" : "/signup"; return; }
  if (!user) { window.location.href = "/signup?next=pricing"; return; }
  setLoading(true);
  try {
    const { data } = await http.post("/billing/checkout", { plan: t.id, billing: annual ? "annual" : "monthly", origin_url: window.location.origin });
    window.location.href = data.url;
  } catch (e) {
    alert("Checkout failed: " + (e.response?.data?.detail || e.message));
    setLoading(false);
  }
}

function PricingCard({ t, annual, user }) {
  const [loading, setLoading] = useState(false);
  const start = () => startCheckout(t, annual, user, setLoading);
  return (
    <div data-testid={`pricing-tier-${t.id}`} className={`border bg-card p-8 flex flex-col ${t.popular ? "border-accent" : "border-border"}`}>
      {t.popular && <div className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Most popular</div>}
      <h3 className="font-display font-bold text-2xl">{t.name}</h3>
      <p className="text-sm text-muted-foreground mb-5">{t.blurb}</p>
      <div className="flex items-end gap-1 mb-1">
        <span className="font-display font-black text-5xl">${t.price}</span>
        <span className="text-muted-foreground mb-2">/{annual ? "yr" : "mo"}</span>
      </div>
      <p className="text-sm font-mono text-accent mb-6">{t.credits} verifications / mo</p>
      <button onClick={start} disabled={loading} className={`${t.popular ? "btn-accent" : "btn-primary"} py-3 mb-6`} data-testid={`pricing-cta-${t.id}`}>
        {loading ? "Loading…" : t.cta}
      </button>
      <ul className="space-y-3 text-sm">
        {t.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="text-valid mt-0.5 shrink-0" size={16} /> {f}</li>)}
      </ul>
    </div>
  );
}

export function About() {
  return (
    <Shell>
      <Seo title="About" description="Verix is on a mission to make data verification fast, honest and accessible — email, phone and IP risk scoring without the enterprise tax." path="/about" />
      <Section className="py-20 max-w-3xl">
        <h1 className="font-display font-black text-5xl tracking-tighter mb-6">We think bad data is a solved problem.</h1>
        <div className="prose-invert space-y-5 text-muted-foreground text-lg leading-relaxed">
          <p>Verix started from a simple frustration: verifying a user shouldn't require three vendors, three invoices and a sales call. Email goes to one tool, phone to another, IP risk to a third — each with its own credit system and contract.</p>
          <p>We built Verix to collapse all of it into one fast API and one universal credit. Real DNS lookups. Real carrier data. Real proxy detection. A single 0–100 verdict your code can act on instantly.</p>
          <p>And we made it accessible: a genuinely useful free tier, SMB-friendly pricing, and a tool you can use in your browser the second you arrive — no SDK, no onboarding call.</p>
        </div>
        <div className="grid grid-cols-3 gap-6 mt-12">
          {[["3-in-1", "Signals per credit"], ["<5 min", "To first verification"], ["0", "Sales calls required"]].map(([v, l]) => (
            <div key={l} className="border border-border p-6 text-center"><div className="font-display font-black text-3xl text-accent">{v}</div><div className="text-sm text-muted-foreground mt-1">{l}</div></div>
          ))}
        </div>
      </Section>
    </Shell>
  );
}

export function Contact() {
  const [sent, setSent] = useState(false);
  return (
    <Shell>
      <Seo title="Contact" description="Get in touch with the Verix team about verification, fraud prevention, volume pricing or partnerships." path="/contact" />
      <Section className="py-20 max-w-xl">
        <h1 className="font-display font-black text-5xl tracking-tighter mb-4">Talk to us.</h1>
        <p className="text-muted-foreground mb-8">Questions about volume pricing, integrations or fraud strategy? Drop us a line.</p>
        {sent ? (
          <div className="border border-valid p-6 text-valid font-mono" data-testid="contact-success">✓ Thanks — we'll reply within one business day.</div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); setSent(true); }} className="space-y-4" data-testid="contact-form">
            <input required placeholder="Your name" className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="contact-name" />
            <input required type="email" placeholder="Work email" className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="contact-email" />
            <textarea required placeholder="How can we help?" rows={5} className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="contact-message" />
            <button className="btn-accent px-6 py-3 w-full" data-testid="contact-submit">Send message</button>
          </form>
        )}
      </Section>
    </Shell>
  );
}

export function UseCases() {
  const [items, setItems] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { http.get("/use-cases").then((r) => setItems(r.data.items)); }, []);
  return (
    <Shell>
      <Seo title="Use Cases" description="See how teams use Verix to stop SaaS signup fraud, cut e-commerce chargebacks, qualify leads and protect email deliverability." path="/use-cases" />
      <Section className="py-20">
        <h1 className="font-display font-black text-5xl tracking-tighter mb-4">Built for every fraud frontline.</h1>
        <p className="text-muted-foreground text-lg max-w-2xl mb-12">Wherever bad data enters your business, Verix scores it in real time.</p>
        <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
          {items.map((u) => (
            <Link key={u.slug} to={`/use-cases/${u.slug}`} className="bg-card p-8 hover:bg-secondary transition-colors group" data-testid={`usecase-${u.slug}`}>
              <h3 className="font-display font-bold text-2xl mb-2 group-hover:text-accent">{u.title}</h3>
              <p className="text-muted-foreground mb-4">{u.headline}</p>
              <span className="text-accent text-sm flex items-center gap-1">Read more <ArrowRight size={15} /></span>
            </Link>
          ))}
        </div>
      </Section>
    </Shell>
  );
}

export function UseCasePage() {
  const { slug } = useParams();
  const [u, setU] = useState(null);
  const load = useCallback(async () => {
    const { data } = await http.get("/use-cases");
    setU(data.items.find((x) => x.slug === slug));
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  if (!u) return <Shell><Section className="py-32 text-center text-muted-foreground">Loading…</Section></Shell>;
  return (
    <Shell>
      <Seo title={u.title} description={`${u.headline} ${u.solution}`} path={`/use-cases/${slug}`} />
      <Section className="py-20 max-w-3xl">
        <Link to="/use-cases" className="text-sm text-muted-foreground hover:text-accent">← All use cases</Link>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-4 mb-3">{u.title}</h1>
        <p className="text-2xl text-accent font-display mb-8">{u.headline}</p>
        <div className="space-y-6 text-lg text-muted-foreground">
          <div><span className="text-foreground font-display font-bold">The problem.</span> {u.problem}</div>
          <div><span className="text-foreground font-display font-bold">How Verix helps.</span> {u.solution}</div>
        </div>
        <div className="mt-8 border border-border p-6">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Key signals</p>
          <ul className="space-y-2">{u.signals.map((s) => <li key={s} className="flex items-center gap-2"><Check className="text-valid" size={16} /> {s}</li>)}</ul>
        </div>
        <div className="mt-10"><VerifyWidget demo /></div>
        <Link to="/signup" className="btn-accent px-6 py-3 mt-8 inline-flex items-center gap-2">Start free <ArrowRight /></Link>
      </Section>
    </Shell>
  );
}

export { Shell };
